import React, { useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Button, Modal } from "react-bootstrap";
import cogoToast from "cogo-toast";
import { permissionCssClass, ucFirst } from "../../../../../../../util/Helpers";
import { Permissions, PermissionType } from "../../../../../../../util/Permissions";
import { useAllowUserPinResetMutation, useClearUserPinSuspensionMutation, useGetUserPinStatusQuery } from "../../../../../../../../redux/apiSlice";

const Pin = (props) => {
    const adminUser = useSelector(({ auth }) => (auth.user), shallowEqual);
    const userId = props.match.params.userId;
    const [confirmModal, setConfirmModal] = useState({ show: false, action: null });

    const {
        data: pinStatusResponse,
        isFetching: isFetchingPinStatus,
        error: pinStatusError,
        refetch: refetchPinStatus,
    } = useGetUserPinStatusQuery(
        { userId },
        { skip: !userId }
    );
    const [clearPinSuspension, { isLoading: isClearingPinSuspension }] = useClearUserPinSuspensionMutation();
    const [allowPinReset, { isLoading: isAllowingPinReset }] = useAllowUserPinResetMutation();

    useEffect(() => {
        if (pinStatusError) cogoToast.error(pinStatusError?.data || `${pinStatusError}`);
    }, [pinStatusError]);

    const pinStatusData = useMemo(() => {
        const payload = pinStatusResponse?.data ?? pinStatusResponse ?? null;
        const data = payload?.data ?? payload;
        return data;
    }, [pinStatusResponse]);

    const formatDateTime = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return `${value}`;
        return d.toLocaleString();
    };

    const asYesNo = (value) => (value === true ? "Yes" : value === false ? "No" : "-");

    const pinStatusRaw = pinStatusData?.pinStatus ? `${pinStatusData.pinStatus}`.toUpperCase() : "";
    const pinStatusLabel = pinStatusRaw ? ucFirst(pinStatusRaw.toLowerCase()) : "-";
    const pinStatusVariant = pinStatusRaw === "ACTIVE" ? "success" : pinStatusRaw === "INACTIVE" ? "danger" : "warning";
    const hasPinLabel = asYesNo(pinStatusData?.hasPin);
    const hasPinVariant = hasPinLabel === "Yes" ? "success" : hasPinLabel === "No" ? "danger" : "warning";

    const pinDisplayItems = useMemo(() => ([
        { label: "Has PIN", value: asYesNo(pinStatusData?.hasPin) },
        { label: "Status", value: pinStatusData?.pinStatus ? ucFirst(`${pinStatusData.pinStatus}`.toLowerCase()) : "-" },
        { label: "Attempts Remaining", value: pinStatusData?.attemptsRemaining ?? "-" },
        { label: "Failed Attempts", value: pinStatusData?.failedAttempts ?? "-" },
        { label: "PIN Expires At", value: formatDateTime(pinStatusData?.pinExpiresAt) },
        { label: "Days Until Expiry", value: pinStatusData?.daysUntilExpiry ?? "-" },
        { label: "Last Transaction PIN Failed", value: formatDateTime(pinStatusData?.pinTxnLastFailedAt) },
        { label: "Last Transaction PIN Verified", value: formatDateTime(pinStatusData?.pinTxnLastVerifiedAt) },
    ]), [pinStatusData]);

    const closeConfirmModal = () => setConfirmModal({ show: false, action: null });

    const confirmClearPinSuspension = () => setConfirmModal({ show: true, action: "clearSuspension" });
    const confirmAllowPinReset = () => setConfirmModal({ show: true, action: "allowReset" });

    const executePinAction = async () => {
        if (!userId || !confirmModal.action) return;
        try {
            if (confirmModal.action === "clearSuspension") {
                await clearPinSuspension({ userId }).unwrap();
                cogoToast.success("PIN suspension cleared");
            } else if (confirmModal.action === "allowReset") {
                await allowPinReset({ userId }).unwrap();
                cogoToast.success("PIN reset allowed");
            }
            closeConfirmModal();
            refetchPinStatus();
        } catch (e) {
            cogoToast.error(e?.data || `${e}`);
        }
    };

    return (
        <div className="row">
            <div className="col-12">
                <div className="card card-custom gutter-b">
                    <div className="card-header border-0">
                        <h3 className="card-title font-weight-bolder text-dark">PIN</h3>
                        <div className={`card-toolbar d-flex ${permissionCssClass(adminUser, Permissions.PROFILE, PermissionType.UPDATE)}`}>
                            <Button
                                type="button"
                                variant="light"
                                className="btn-sm mr-2"
                                onClick={confirmClearPinSuspension}
                                disabled={isClearingPinSuspension || isAllowingPinReset}
                            >
                                Clear Suspension
                            </Button>
                            <Button
                                type="button"
                                variant="light"
                                className="btn-sm"
                                onClick={confirmAllowPinReset}
                                disabled={isClearingPinSuspension || isAllowingPinReset}
                            >
                                Allow Reset
                            </Button>
                        </div>
                    </div>
                    <div className="card-body pt-2">
                        {isFetchingPinStatus ? (
                            <span className="text-muted font-weight-bold font-size-sm">Loading...</span>
                        ) : (
                            <>
                                <div className="row mb-4">
                                    <div className="col-md-3 mb-4 mb-md-0">
                                        <div className={`card card-custom bg-light-${hasPinVariant} border-0 shadow-none h-100 mb-0`}>
                                            <div className="card-body py-5">
                                                <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-2">Has PIN</div>
                                                <div className="font-size-h5 font-weight-bolder text-dark mb-0">{hasPinLabel}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-4 mb-md-0">
                                        <div className={`card card-custom bg-light-${pinStatusVariant} border-0 shadow-none h-100 mb-0`}>
                                            <div className="card-body py-5">
                                                <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-2">Status</div>
                                                <div className="font-size-h5 font-weight-bolder text-dark mb-0">{pinStatusLabel}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3 mb-4 mb-md-0">
                                        <div className="card card-custom bg-light-primary border-0 shadow-none h-100 mb-0">
                                            <div className="card-body py-5">
                                                <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-2">Attempts Remaining</div>
                                                <div className="font-size-h5 font-weight-bolder text-dark mb-0">{pinStatusData?.attemptsRemaining ?? "-"}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card card-custom bg-light-danger border-0 shadow-none h-100 mb-0">
                                            <div className="card-body py-5">
                                                <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-2">Failed Attempts</div>
                                                <div className="font-size-h5 font-weight-bolder text-dark mb-0">{pinStatusData?.failedAttempts ?? "-"}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="separator separator-dashed my-5"></div>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="p-4 rounded bg-light-secondary h-100">
                                            <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-3">Expiry</div>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted font-weight-bold font-size-sm">PIN Expires At</span>
                                                <span className="text-dark font-weight-bolder font-size-sm">{formatDateTime(pinStatusData?.pinExpiresAt)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted font-weight-bold font-size-sm">Days Until Expiry</span>
                                                <span className="text-dark font-weight-bolder font-size-sm">{pinStatusData?.daysUntilExpiry ?? "-"}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="p-4 rounded bg-light-secondary h-100">
                                            <div className="font-size-sm text-muted font-weight-bold text-uppercase mb-3">Last Activity</div>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="text-muted font-weight-bold font-size-sm">Last Failed</span>
                                                <span className="text-dark font-weight-bolder font-size-sm">{formatDateTime(pinStatusData?.pinTxnLastFailedAt)}</span>
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-muted font-weight-bold font-size-sm">Last Verified</span>
                                                <span className="text-dark font-weight-bolder font-size-sm">{formatDateTime(pinStatusData?.pinTxnLastVerifiedAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <Modal show={confirmModal.show} onHide={closeConfirmModal} centered backdrop={"static"}>
                <Modal.Header closeButton={false}>
                    <Modal.Title>
                        {confirmModal.action === "clearSuspension" ? "Clear PIN Suspension" : "Allow PIN Reset"}
                    </Modal.Title>
                    <button type="button" className="close" onClick={closeConfirmModal} disabled={isClearingPinSuspension || isAllowingPinReset}>
                        <span aria-hidden="true" className="d-block">×</span>
                        <span className="sr-only">Close</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-6">
                        {confirmModal.action === "clearSuspension"
                            ? "Are you sure you want to clear this user's PIN suspension?"
                            : "Are you sure you want to allow this user to reset their PIN?"}
                    </div>
                    <div className="d-flex">
                        <Button
                            type="button"
                            variant="outline-dark"
                            className="flex-fill mr-3"
                            onClick={closeConfirmModal}
                            disabled={isClearingPinSuspension || isAllowingPinReset}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="dark"
                            className="flex-fill"
                            onClick={executePinAction}
                            disabled={isClearingPinSuspension || isAllowingPinReset}
                        >
                            Confirm
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Pin;
